import {
  BehaviorSubject,
  delay,
  filter,
  merge,
  tap,
  type Observable,
} from 'rxjs';
import { call, getWampSessionId } from 'sync/wamp';
import { WAMPCallError } from 'sync/SimpleWAMP';
import type { ClientRequest, UserEvent } from 'sync/types';
import type {
  ChatMessageContentEvent,
  ChatMessageToolCallEvent,
  ChatMessageToolResultEvent,
} from './types';
import { collectRenders, type RenderStore } from './renderMetadata';
import {
  websocketChatMessageDone$,
  websocketChatMessageError$,
} from './websocketStreams$';

const WATCH_AGENT_RUN_PROCEDURE = 'https://app.ardoq.com/rpc#watch';
const NO_ACTIVE_AGENT_RUN_ERROR =
  'https://app.ardoq.com/error#no-active-agent-run';

type WatchAgentRunResult = {
  sessionId: string;
};

const requestAgentRunWatch = (
  sessionId: string
): Promise<WatchAgentRunResult> =>
  call<WatchAgentRunResult>(WATCH_AGENT_RUN_PROCEDURE, sessionId);

export const isNoActiveAgentRunError = (error: unknown): boolean =>
  error instanceof WAMPCallError &&
  error.errorUri === NO_ACTIVE_AGENT_RUN_ERROR;

type AgentRunProgress = {
  // Latest complete content snapshot received for this exchange.
  message?: string;
  // Tool calls retained by protocol ID for deduplication and UI restoration.
  toolCallsById: Map<string, ChatMessageToolCallEvent>;
  renderMetadata: RenderStore;
};

export type AgentRunProgressSnapshot = {
  message?: string;
  toolCalls: ChatMessageToolCallEvent[];
  renderMetadata: RenderStore;
};

export type AgentRun = {
  // Chat session owning the single currently active exchange.
  sessionId: string;
  exchangeId: string;
  // The WAMP connection instance on which this session's watch was confirmed.
  // A reconnect receives a new WAMP session ID, making the previous value stale.
  attachedWampSessionId?: number;
  progress: AgentRunProgress;
};

const agentRunsBySessionId = new Map<string, AgentRun>();
type AttachmentRequest = {
  wampSessionId: number;
  promise: Promise<{ sessionId: string }>;
};

const attachmentRequestsBySessionId = new Map<string, AttachmentRequest>();
const activeAgentRunSessionIdsSubject = new BehaviorSubject<
  ReadonlySet<string>
>(new Set());

export const activeAgentRunSessionIds$ =
  activeAgentRunSessionIdsSubject.asObservable();

const emitWatchedSessions = (): void => {
  activeAgentRunSessionIdsSubject.next(new Set(agentRunsBySessionId.keys()));
};

const findAgentRunByExchangeId = (exchangeId: string): AgentRun | undefined =>
  [...agentRunsBySessionId.values()].find(run => run.exchangeId === exchangeId);

export const rememberAgentRun = (
  sessionId: string,
  exchangeId: string
): void => {
  const existingRun = agentRunsBySessionId.get(sessionId);
  if (existingRun?.exchangeId === exchangeId) return;

  agentRunsBySessionId.set(sessionId, {
    sessionId,
    exchangeId,
    progress: { toolCallsById: new Map(), renderMetadata: {} },
  });
  attachmentRequestsBySessionId.delete(sessionId);
  emitWatchedSessions();
};

export const attachAgentRun = (
  sessionId: string
): Promise<{ sessionId: string }> => {
  const run = agentRunsBySessionId.get(sessionId);
  const wampSessionId = getWampSessionId();
  if (!run || wampSessionId === null) {
    return requestAgentRunWatch(sessionId);
  }

  if (run.attachedWampSessionId === wampSessionId) {
    return Promise.resolve({ sessionId });
  }

  const pendingRequest = attachmentRequestsBySessionId.get(sessionId);
  if (pendingRequest?.wampSessionId === wampSessionId) {
    return pendingRequest.promise;
  }

  const exchangeId = run.exchangeId;
  const request = requestAgentRunWatch(sessionId)
    .then(result => {
      const currentRun = agentRunsBySessionId.get(sessionId);
      if (
        currentRun?.exchangeId === exchangeId &&
        getWampSessionId() === wampSessionId
      ) {
        currentRun.attachedWampSessionId = wampSessionId;
      }
      return result;
    })
    .finally(() => {
      if (attachmentRequestsBySessionId.get(sessionId)?.promise === request) {
        attachmentRequestsBySessionId.delete(sessionId);
      }
    });

  attachmentRequestsBySessionId.set(sessionId, {
    wampSessionId,
    promise: request,
  });
  return request;
};

export const getAgentRuns = (): readonly AgentRun[] => [
  ...agentRunsBySessionId.values(),
];

export const isAgentRunExchangeTracked = (exchangeId: string): boolean =>
  findAgentRunByExchangeId(exchangeId) !== undefined;

export const isAgentRunExchangeTrackedForSession = (
  exchangeId: string,
  sessionId: string | null
): boolean =>
  sessionId !== null &&
  agentRunsBySessionId.get(sessionId)?.exchangeId === exchangeId;

export const recordAgentRunContentEvent = (
  event: UserEvent<ClientRequest<ChatMessageContentEvent>>
): void => {
  const exchangeId = event.data.exchangeId;
  if (typeof exchangeId !== 'string') return;
  const run = findAgentRunByExchangeId(exchangeId);
  if (!run) return;
  run.progress.message = event.data.message;
};

export const recordAgentRunToolCallEvent = (
  event: UserEvent<ClientRequest<ChatMessageToolCallEvent>>
): void => {
  const exchangeId = event.data.exchangeId;
  if (typeof exchangeId !== 'string') return;
  const run = findAgentRunByExchangeId(exchangeId);
  if (!run) return;
  run.progress.toolCallsById.set(event.data.tool_call_id, event.data);
};

export const recordAgentRunToolResultEvent = (
  event: UserEvent<ClientRequest<ChatMessageToolResultEvent>>
): void => {
  const { exchangeId, metadata } = event.data;
  if (typeof exchangeId !== 'string' || metadata === undefined) return;
  const run = findAgentRunByExchangeId(exchangeId);

  if (!run) return;

  run.progress.renderMetadata = {
    ...run.progress.renderMetadata,
    ...collectRenders([metadata]),
  };
};

export const getAgentRunProgressSnapshot = (
  exchangeId: string
): AgentRunProgressSnapshot => {
  const run = findAgentRunByExchangeId(exchangeId);
  return {
    message: run?.progress.message,
    toolCalls: run ? [...run.progress.toolCallsById.values()] : [],
    renderMetadata: run?.progress.renderMetadata ?? {},
  };
};

export const forgetAgentRunByExchangeId = (exchangeId: string): void => {
  const run = findAgentRunByExchangeId(exchangeId);
  if (run) forgetAgentRunBySessionId(run.sessionId);
};

export const forgetAgentRunBySessionId = (sessionId: string): void => {
  if (!agentRunsBySessionId.delete(sessionId)) return;
  attachmentRequestsBySessionId.delete(sessionId);
  emitWatchedSessions();
};

export const clearAgentRuns = (): void => {
  if (agentRunsBySessionId.size === 0) return;
  agentRunsBySessionId.clear();
  attachmentRequestsBySessionId.clear();
  emitWatchedSessions();
};

type DurableTerminalEvent = UserEvent<
  ClientRequest<unknown> & { exchangeId: string }
>;

const hasExchangeId = (
  event: UserEvent<ClientRequest<unknown>>
): event is DurableTerminalEvent => typeof event.data.exchangeId === 'string';

export const agentRunRegistryLifecycle$: Observable<DurableTerminalEvent> =
  merge(websocketChatMessageDone$, websocketChatMessageError$).pipe(
    filter(hasExchangeId),
    delay(0),
    tap(event => forgetAgentRunByExchangeId(event.data.exchangeId))
  );
